/**
 * AnnotatedDiagram — an exploded-view image with numbered hotspots that are
 * bidirectionally linked to material part cards (CX-2728 "Monomaterial Headlamp
 * Concept").
 *
 * Interaction (Jira AC): selecting a hotspot highlights its card and vice-versa.
 * We support BOTH hover (desktop) and click/tap (works on touch, where hover is
 * unavailable) — the highlighted part is `hovered ?? clicked`. Clicking an
 * already-active part toggles it off. `active === part.number` drives the
 * highlight on both the hotspot dot and its card.
 */

import { ContentLink } from "components/content-link";
import { useState } from "react";
import type { AnnotatedDiagram as AnnotatedDiagramData } from "../chapter.types";
import "./AnnotatedDiagram.css";

interface AnnotatedDiagramProps {
  title: string;
  diagram: AnnotatedDiagramData;
}

export const AnnotatedDiagram = ({ title, diagram }: AnnotatedDiagramProps) => {
  const [clicked, setClicked] = useState<number | null>(null);
  const [hovered, setHovered] = useState<number | null>(null);

  // Hover wins while pointing; otherwise the last click sticks (touch support).
  const active = hovered ?? clicked;

  const toggleClick = (num: number) =>
    setClicked((prev) => (prev === num ? null : num));

  return (
    <section className="cmf-diagram">
      <div className="cmf-diagram__header">
        <span className="cmf-diagram__accent" aria-hidden="true" />
        <h2 className="cmf-diagram__title">{title}</h2>
      </div>

      {diagram.introParagraphs?.map((paragraph) => (
        <p key={paragraph} className="cmf-diagram__intro">
          {paragraph}
        </p>
      ))}

      {/* Exploded-view image with numbered hotspots */}
      <div className="cmf-diagram__figure">
        <img
          className="cmf-diagram__image"
          src={diagram.image}
          alt={diagram.imageAlt ?? title}
        />
        {diagram.parts.map((part) => {
          const isActive = active === part.number;
          return (
            <button
              type="button"
              key={part.number}
              className={`cmf-diagram__hotspot${
                isActive ? " cmf-diagram__hotspot--active" : ""
              }`}
              style={{
                left: `${part.hotspot.left}%`,
                top: `${part.hotspot.top}%`,
              }}
              onMouseEnter={() => setHovered(part.number)}
              onMouseLeave={() => setHovered(null)}
              onClick={() => toggleClick(part.number)}
              aria-pressed={isActive}
              aria-label={`${part.number}. ${part.part} — ${part.material}`}
            >
              {part.number}
            </button>
          );
        })}
      </div>

      {/* Material part cards. The card itself is NOT a control — it holds a
          real link (ContentLink) and would nest interactives if it were a
          button. Hover (desktop) + click (touch) only drive the visual
          highlight sync; keyboard users reach the same parts via the hotspot
          buttons above and the card's own "View in Imagio" link. */}
      <div className="cmf-diagram__cards">
        {diagram.parts.map((part) => {
          const isActive = active === part.number;
          return (
            // biome-ignore lint/a11y/useKeyWithClickEvents: highlight is a pointer/touch enhancement; hotspot buttons + the card link provide the keyboard path.
            // biome-ignore lint/a11y/noStaticElementInteractions: card is not a control (it nests a link); click only toggles the visual highlight for touch users.
            <div
              key={part.number}
              className={`cmf-diagram__card${
                isActive ? " cmf-diagram__card--active" : ""
              }`}
              onMouseEnter={() => setHovered(part.number)}
              onMouseLeave={() => setHovered(null)}
              onClick={() => toggleClick(part.number)}
            >
              <div className="cmf-diagram__card-head">
                <span className="cmf-diagram__card-num" aria-hidden="true">
                  {part.number}
                </span>
                <span className="cmf-diagram__card-part">{part.part}</span>
              </div>
              <span className="cmf-diagram__card-material">
                {part.material}
              </span>
              <span className="cmf-diagram__card-prop">{part.keyProperty}</span>
              {part.link && (
                <ContentLink
                  {...part.link}
                  className="cmf-diagram__card-link"
                />
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};
